/**
 * Drafting API Service
 *
 * Surviving methods after the Plan 184 (#783) cleanup, which removed the
 * legacy full-page drafting flow and the per-clause refinement endpoints.
 *
 * 1. createDraftingProject(config) - Create a new drafting project
 * 2. getDraftingProject(projectId) - Fetch project + clauses by base project id
 * 3. generateDraft(projectId) - Trigger generation
 * 4. getGenerationStatus(projectId) - Poll status
 * 5. exportWord(projectId) - Download DOCX blob
 * 6. uploadBaseDocument(projectId, file, onProgress) - Upload playbook for RAG
 * 7. getBaseDocumentStatus(projectId) - Poll RAG processing
 */

import { apiService } from './api';
import { getErrorMessage } from '../utils/helpers';
import {
  DraftingProject,
  DraftingProjectConfig,
  GenerationResponse,
  GenerationStatus,
  BaseDocumentUploadResponse,
  BaseDocumentStatus,
  PrecedentResponse,
  PrecedentListResponse,
  PrecedentStatusResponse,
  ReplacePrimaryResponse,
} from '../types/drafting';

export class DraftingApiService {
  private baseUrl = '/api/v1/drafting';

  async createDraftingProject(config: DraftingProjectConfig): Promise<DraftingProject> {
    try {
      const response = await apiService.post<DraftingProject>(
        `${this.baseUrl}/projects`,
        config
      );

      if (!response) {
        throw new Error('No project created');
      }

      return response;
    } catch (error) {
      console.error('Error creating drafting project:', error);
      throw new Error(`Failed to create drafting project: ${getErrorMessage(error)}`);
    }
  }

  async getDraftingProject(projectId: string): Promise<DraftingProject> {
    try {
      const response = await apiService.get<DraftingProject>(
        `${this.baseUrl}/projects/by-project/${projectId}`
      );

      if (!response) {
        throw new Error('No project received');
      }

      return response;
    } catch (error) {
      console.error('Error fetching drafting project:', error);
      throw new Error(`Failed to fetch drafting project: ${getErrorMessage(error)}`);
    }
  }

  /**
   * `requirePrimary` tells the server a primary precedent was staged, so a
   * primary that vanished before generate fails with no_primary_precedent
   * instead of silently drafting from the template alone.
   */
  async generateDraft(
    projectId: string,
    options: { requirePrimary?: boolean } = {},
  ): Promise<GenerationResponse> {
    try {
      const response = await apiService.post<GenerationResponse>(
        `${this.baseUrl}/projects/${projectId}/generate`,
        { require_primary: options.requirePrimary ?? false },
      );

      if (!response) {
        throw new Error('No generation response received');
      }

      return response;
    } catch (error) {
      console.error('Error triggering draft generation:', error);
      throw new Error(`Failed to trigger draft generation: ${getErrorMessage(error)}`);
    }
  }

  async getGenerationStatus(projectId: string): Promise<GenerationStatus> {
    try {
      const response = await apiService.get<GenerationStatus>(
        `${this.baseUrl}/projects/${projectId}/generation/status`
      );

      if (!response) {
        throw new Error('No generation status received');
      }

      return response;
    } catch (error) {
      console.error('Error fetching generation status:', error);
      throw new Error(`Failed to fetch generation status: ${getErrorMessage(error)}`);
    }
  }

  async exportWord(projectId: string): Promise<Blob> {
    try {
      const response = await apiService.get<Blob>(
        `${this.baseUrl}/projects/${projectId}/export/word`,
        { responseType: 'blob' }
      );

      if (!response) {
        throw new Error('No export response received');
      }

      return response;
    } catch (error) {
      console.error('Error exporting Word document:', error);
      throw new Error(`Failed to export Word document: ${getErrorMessage(error)}`);
    }
  }

  /** Plan 255: retry saving the served draft to matter documents after a failure. */
  async retryOutputDocument(projectId: string): Promise<{ status: string; output_id: string }> {
    try {
      return await apiService.post<{ status: string; output_id: string }>(
        `${this.baseUrl}/projects/${projectId}/output-document/retry`,
        {},
      );
    } catch (error) {
      console.error('Error retrying draft output document:', error);
      throw new Error(`Failed to retry saving the draft: ${getErrorMessage(error)}`);
    }
  }

  async uploadBaseDocument(
    projectId: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<BaseDocumentUploadResponse> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiService.post<BaseDocumentUploadResponse>(
        `${this.baseUrl}/projects/${projectId}/base-document`,
        formData
      );

      if (!response) {
        throw new Error('No upload response received');
      }

      if (onProgress) {
        onProgress(100);
      }

      return response;
    } catch (error) {
      console.error('Error uploading base document:', error);
      throw new Error(`Failed to upload base document: ${getErrorMessage(error)}`);
    }
  }

  async getBaseDocumentStatus(projectId: string): Promise<BaseDocumentStatus> {
    try {
      const response = await apiService.get<BaseDocumentStatus>(
        `${this.baseUrl}/projects/${projectId}/base-document/status`
      );

      if (!response) {
        throw new Error('No base document status received');
      }

      return response;
    } catch (error) {
      console.error('Error fetching base document status:', error);
      throw new Error(`Failed to fetch base document status: ${getErrorMessage(error)}`);
    }
  }

  // ── Plan 185: precedent CRUD ────────────────────────────────────────────

  async attachWorkbenchPrecedent(
    projectId: string,
    body: { workbench_document_id: string; role: 'primary' | 'supporting' }
  ): Promise<PrecedentResponse> {
    const response = await apiService.post<PrecedentResponse>(
      `${this.baseUrl}/projects/${projectId}/precedents/from-workbench`,
      body
    );
    if (!response) throw new Error('No precedent response received');
    return response;
  }

  async uploadPrecedent(
    projectId: string,
    file: File,
    opts: { role: 'primary' | 'supporting' }
  ): Promise<BaseDocumentUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('role', opts.role);
    const response = await apiService.post<BaseDocumentUploadResponse>(
      `${this.baseUrl}/projects/${projectId}/precedents/upload`,
      formData
    );
    if (!response) throw new Error('No upload response received');
    return response;
  }

  async listPrecedents(projectId: string): Promise<PrecedentListResponse> {
    const response = await apiService.get<PrecedentListResponse>(
      `${this.baseUrl}/projects/${projectId}/precedents`
    );
    if (!response) throw new Error('No precedent list received');
    return response;
  }

  async getPrecedentStatus(
    projectId: string,
    precedentId: string
  ): Promise<PrecedentStatusResponse> {
    const response = await apiService.get<PrecedentStatusResponse>(
      `${this.baseUrl}/projects/${projectId}/precedents/${precedentId}/status`
    );
    if (!response) throw new Error('No precedent status received');
    return response;
  }

  async deletePrecedent(projectId: string, precedentId: string): Promise<void> {
    await apiService.delete(
      `${this.baseUrl}/projects/${projectId}/precedents/${precedentId}`
    );
  }

  async replacePrimaryWorkbench(
    projectId: string,
    workbenchDocumentId: string
  ): Promise<ReplacePrimaryResponse> {
    const formData = new FormData();
    formData.append('source_type', 'workbench');
    formData.append('workbench_document_id', workbenchDocumentId);
    const response = await apiService.put<ReplacePrimaryResponse>(
      `${this.baseUrl}/projects/${projectId}/precedents/primary`,
      formData
    );
    if (!response) throw new Error('No replace-primary response received');
    return response;
  }

  async replacePrimaryUploaded(
    projectId: string,
    file: File
  ): Promise<ReplacePrimaryResponse> {
    const formData = new FormData();
    formData.append('source_type', 'uploaded');
    formData.append('file', file);
    const response = await apiService.put<ReplacePrimaryResponse>(
      `${this.baseUrl}/projects/${projectId}/precedents/primary`,
      formData
    );
    if (!response) throw new Error('No replace-primary response received');
    return response;
  }
}

export const draftingApiService = new DraftingApiService();

export default draftingApiService;
